/**
 * 관세율표 품명 정확 일치 — 검색어가 현행 관세율표 10자리 세번의 한글·영문 명칭과 같으면 그 세번을 카드가 먼저
 * 보여 준다(커피분쇄기 → 8509.80-1000). 다만 이름은 부모 호의 조건 아래 있으므로(방사선 방호복은 제4015호
 * 고무제 의류일 때만) 결정트리 범위 밖이면 조건부 후보로만 두고, 같은 이름이 여러 호에 있으면(장미 → 0602·0603)
 * 후보를 나란히 보인다. 확정은 결정트리(PROVE)만 한다.
 * 검색어가 세번 이름으로 **끝나면**(`컴퓨터용 USB 유선 광학 마우스` → `마우스` 8471.60-1030) 앞은 수식어로 보고
 * `modifier: true` 로 일치시킨다 — 띄어쓰기 경계가 있을 때만(`조미김`은 `김`으로 끝나지만 한 낱말이라 아니다). 2026-09-15.
 * 글자로는 못 맞춰도 **서버가 동의어 사전을 거쳐 "토큰을 이어 붙이면 이름 전체"라고 표시한 세번(`name_matches`)이면**
 * (`커피그라인더` → 커피분쇄기 8509.80-1000) `synonym: true` 로 일치시킨다. 이름 안에 토큰이 있기만 한 것은 아니다.
 * 엔진이 이미 내린 결정론 판단을 카드가 버리지 않기 위한 것이다(2026-09-15 사용자: "같은 의미의 단어인데 인식을 못 한다").
 */
const digits = (value) => String(value || "").replace(/\D/g, "");

/** 정확 일치 정규화에서는 괄호 안의 법정 조건을 보존한다. */
export function normalizeTariffName(text) {
  return String(text || "")
    .replace(/\/\s*양허유형.*$/u, "")
    .replace(/[^\p{L}\p{N}]+/gu, "")
    .toLowerCase();
}

/**
 * @param q 검색어
 * @param items tariff-search items({ hsk, hs4, chapter, desc_ko, desc })
 * @param engine 관세율표 검색 응답의 { nameMatches } — 있으면 서버 동의어 일치도 본다
 * @returns [{ hsk, hs4, chapter, ko, en, via: "ko"|"en"|"engine", qualified?, modifier?, synonym? }] — 조건을 생략한 검색 후보는 qualified 표시
 */
export function tariffNameMatches(q, items, engine = null) {
  const needle = normalizeTariffName(q);
  if (!needle || needle.length < 2) return [];
  // 꼬리 후보: 마지막 1~3 낱말(띄어쓰기 기준). 검색어 전체는 제외(그건 정확 일치).
  const words = String(q || "").trim().split(/\s+/).filter(Boolean);
  const tails = [];
  for (let k = 1; k <= Math.min(3, words.length - 1); k += 1) {
    const t = normalizeTariffName(words.slice(-k).join(" "));
    if (t && t.length >= 2 && t !== needle) tails.push(t);
  }
  const seen = new Set();
  const out = [];
  for (const item of Array.isArray(items) ? items : []) {
    const hsk = digits(item?.hsk);
    if (hsk.length !== 10 || seen.has(hsk)) continue;
    const ko = String(item?.desc_ko || "");
    const en = String(item?.desc || "");
    const searchName = (text) => normalizeTariffName(String(text).replace(/[（(][^()（）]*[)）]/gu, "").replace(/[[［][^\]］]*[\]］]/gu, ""));
    const exactVia = normalizeTariffName(ko) === needle ? "ko" : normalizeTariffName(en) === needle ? "en" : null;
    let via = exactVia || (searchName(ko) === needle ? "ko" : searchName(en) === needle ? "en" : null);
    let modifier = false;
    let modifierQualified = false;
    if (!via) {
      const nk = normalizeTariffName(ko); const ne = normalizeTariffName(en); const sk = searchName(ko); const se = searchName(en);
      const tail = tails.find((t) => t === nk || t === ne || t === sk || t === se);
      if (tail) { modifier = true; via = tail === nk || tail === sk ? "ko" : "en"; modifierQualified = !(tail === nk || tail === ne); }
    }
    if (!via) continue;
    seen.add(hsk);
    out.push({
      hsk,
      hs4: digits(item?.hs4) || hsk.slice(0, 4),
      chapter: String(item?.chapter || hsk.slice(0, 2)).padStart(2, "0"),
      ko: ko.replace(/\/\s*양허유형.*$/u, "").trim(),
      en: en.trim(),
      via,
      ...((modifier ? modifierQualified : !exactVia) ? { qualified: true } : {}),
      ...(modifier ? { modifier: true } : {}),
    });
  }
  if (out.length === 0) {
    const e = engineFullMatch(q, items, engine);
    if (e) out.push(e);
  }
  if (out.length === 0) {
    const l = llmNameMatch(items, engine);
    if (l) out.push(l);
  }
  return out.sort((a, b) => a.hsk.localeCompare(b.hsk));
}

/**
 * LLM이 읽은 법령어 품명 경로(2026-09-15) — 제안 신호의 `name_ko`(예: 커피그라인드 → 커피분쇄기)로 관세율표를 한 번 더 찾은 결과
 * (`engine.llm = { name, items, nameMatches }`)에서 서버가 그 이름과 같다고 표시한 세번을 `llm: true` 로 받는다. 글자·동의어 일치가
 * 모두 실패했을 때만. LLM은 이름을 제안했을 뿐이고 세번은 관세율표 명칭 비교(서버)가 정한다 — 확정은 여전히 트리.
 */
function llmNameMatch(items, engine) {
  const llm = engine?.llm;
  if (!llm || !llm.name) return null;
  const list = Array.isArray(llm.nameMatches) ? llm.nameMatches : [];
  const rows = Array.isArray(items) ? items : [];
  for (const nm of list) {
    const hsk = digits(nm?.hsk);
    const item = rows.find((it) => digits(it?.hsk) === hsk);
    if (!item) continue;
    const ko = String(item.desc_ko || "");
    return {
      hsk,
      hs4: digits(item.hs4) || hsk.slice(0, 4),
      chapter: String(item.chapter || hsk.slice(0, 2)).padStart(2, "0"),
      ko: ko.replace(/\/\s*양허유형.*$/u, "").trim(),
      en: String(item.desc || "").trim(),
      via: "llm",
      llm: true,
      llmName: String(llm.name),
      ...(nm.via === "synonym" ? { synonym: true } : {}),
    };
  }
  return null;
}

/**
 * 서버 명칭 일치 — 관세율표 검색 응답의 `name_matches[]`(동의어 사전을 거쳐 "토큰을 이어 붙이면 이름 전체"인 세번).
 * 글자 비교가 실패한 세번 중 서버가 synonym 으로 표시한 것만 받는다. 토큰이 이름 안에 들어 있기만 한 경우
 * (휴대용 자동자료처리기계(… 키보드 …) 에 "키보드")는 서버도 일치로 주지 않으므로 8471.30 오판(32차)은 재발하지 않는다.
 */
function engineFullMatch(q, items, engine) {
  const list = Array.isArray(engine?.nameMatches) ? engine.nameMatches : [];
  if (!list.length) return null;
  const rows = Array.isArray(items) ? items : [];
  for (const nm of list) {
    if (!nm || nm.via !== "synonym") continue;
    const hsk = digits(nm.hsk);
    const item = rows.find((it) => digits(it?.hsk) === hsk);
    if (!item) continue;
    const ko = String(item.desc_ko || "");
    return {
      hsk,
      hs4: digits(item.hs4) || hsk.slice(0, 4),
      chapter: String(item.chapter || hsk.slice(0, 2)).padStart(2, "0"),
      ko: ko.replace(/\/\s*양허유형.*$/u, "").trim(),
      en: String(item.desc || "").trim(),
      via: "engine",
      synonym: true,
    };
  }
  return null;
}

/**
 * 일치 결과를 결정트리 범위와 대조해 카드가 어떻게 다룰지 정한다.
 * - single_in_scope: 한 줄이고 트리가 그 호·류를 보고 있다 → 참고 후보와 확인 질문 표시
 * - single_out_of_scope: 한 줄이지만 트리 범위 밖(또는 트리 없음) → 부모 호 조건을 붙인 후보로만
 * - multiple: 같은 이름이 여러 세번 → 후보 나란히, 가르는 질문은 트리가 낸다
 */
export function classifyTariffMatches(matches, scopePrefixes = []) {
  const list = Array.isArray(matches) ? matches : [];
  if (list.length === 0) return { kind: "none", matches: [] };
  if (list.length > 1) return { kind: "multiple", matches: list };
  const prefixes = (Array.isArray(scopePrefixes) ? scopePrefixes : []).map(digits).filter(Boolean);
  const inScope = prefixes.length > 0 && prefixes.some((p) => list[0].hsk.startsWith(p));
  return { kind: inScope ? "single_in_scope" : "single_out_of_scope", matches: list };
}
